import { Component, Element, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { throttle } from "../../utils/throttle";
import { getUniqueId } from "../../utils/unique-id";

interface ElementRects {
    commonParentElement: HTMLElement;
    commonParentRects: DOMRect;
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    targetElements: any;
    calculated: boolean;
}

@Component({
    tag: "ux-same-height"
})
export class SameHeightWrapper {
    private baseClass = "ux-same-height";
    private heightAttribute = "data-same-height";

    @Element() element!: HTMLUxSameHeightElement;

    /** optional: the css selectors for the elements that should have the same height, comma separated string  */
    // @ts-expect-error Check for typing
    @Prop() readonly targetSelectors: string = null;
    /** optional: the css selectors for the common parents to compare for the height calculations */
    // @ts-expect-error Check for typing
    @Prop() readonly itemContainerSelectors: string = null;

    @State() sameHeightIdentifiers: string[] = [];
    @State() elementsRects: ElementRects[] = [];
    @State() itemContainerElements: Element[];

    private setDataAttributeToTarget = () => {
        if (this.targetSelectors === null) {
            return;
        }
        const selectors = this.targetSelectors.split(";");
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < selectors.length; i++) {
            const elements = Array.from(this.element.querySelectorAll(selectors[i]));
            const uid = getUniqueId();
            // @ts-expect-error Check for typing
            elements.forEach((element: HTMLElement) => {
                if (element.hasAttribute(this.heightAttribute)) {
                    return;
                }
                element.setAttribute(this.heightAttribute, uid);
            });
        }
    };

    private setItemContaionerElements = () => {
        if (this.itemContainerSelectors !== null) {
            this.itemContainerElements = Array.from(this.element.querySelectorAll(`${this.itemContainerSelectors}`));
            return;
        }
        const targets = this.element.querySelectorAll(`*[${this.heightAttribute}]`);
        const range = new Range();
        range.setStartBefore(targets[0]);
        range.setEndAfter(targets[targets.length - 1]);
        this.itemContainerElements = Array.from((range.commonAncestorContainer as HTMLElement).children);
    };

    private getElementsRects() {
        const elementsRects: ElementRects[] = [];
        // @ts-expect-error Check for typing
        this.itemContainerElements.forEach((commonParent: HTMLElement) => {
            const commonParentElement = commonParent;
            // do not include in the calculation items that have been hidden from the static filter;
            if (commonParentElement.classList.contains("ux-static-filter__item--hidden")) {
                return;
            }
            const commonParentRects = commonParent.getBoundingClientRect();
            let targetElementsRects = {};
            this.sameHeightIdentifiers.forEach((sameHeightIdentifier) => {
                // @ts-expect-error Check for typing
                const targetElement: HTMLElement = commonParent.querySelector(
                    `*[${this.heightAttribute}="${sameHeightIdentifier}"]`
                );
                if (targetElement !== null) {
                    const targetElementRects = targetElement.getBoundingClientRect();
                    targetElementsRects = {
                        ...targetElementsRects,
                        [sameHeightIdentifier]: {
                            targetElement,
                            targetElementRects
                        }
                    };
                }
            });
            elementsRects.push({
                commonParentElement,
                commonParentRects,
                targetElements: targetElementsRects,
                calculated: false
            });
        });
        this.elementsRects = elementsRects;
    }

    private getSameHeightIdentifiers = () => {
        const sameHeightElements = this.element.querySelectorAll(`*[${this.heightAttribute}]`);
        const identifiers: string[] = [];
        // @ts-expect-error Check for typing
        sameHeightElements.forEach((element: HTMLElement) => {
            // @ts-expect-error Check for typing
            identifiers.push(element.dataset.sameHeight);
        });
        const uniqueIdentifiers: string[] = [];
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < identifiers.length; i++) {
            if (!uniqueIdentifiers.includes(identifiers[i])) {
                uniqueIdentifiers.push(identifiers[i]);
            }
        }
        this.sameHeightIdentifiers = uniqueIdentifiers;
    };

    componentDidLoad() {
        this.setDataAttributeToTarget();
        this.getSameHeightIdentifiers();
        this.setItemContaionerElements();
        this.getElementsRects();
    }

    @Watch("elementsRects")
    updateHeight() {
        this.updateElementsHeight();
    }

    private updateElementsHeight = () => {
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < this.elementsRects.length; i++) {
            const sameLineElements = this.elementsRects.filter((elementRects) => {
                return elementRects.commonParentRects.top === this.elementsRects[i].commonParentRects.top;
            });
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let v = 0; v < this.sameHeightIdentifiers.length; v++) {
                let biggestHeight = 0;
                // eslint-disable-next-line @typescript-eslint/prefer-for-of
                for (let i = 0; i < sameLineElements.length; i++) {
                    const element = sameLineElements[i].targetElements[this.sameHeightIdentifiers[v]];
                    let height = 0;

                    if (element?.targetElementRects) {
                        this.preventVerticalMarginCollapse(element.targetElement);
                        height = element.targetElement.scrollHeight;
                    }
                    if (biggestHeight < height) {
                        biggestHeight = height;
                    }
                }
                sameLineElements.forEach((element) => {
                    const targetElement = element.targetElements[this.sameHeightIdentifiers[v]];
                    if (targetElement) {
                        targetElement.targetElement.style.minHeight = `${biggestHeight}px`;
                    }
                });
            }
        }
    };

    private removeHeights = throttle(() => {
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let v = 0; v < this.sameHeightIdentifiers.length; v++) {
            this.elementsRects.forEach((elementRects) => {
                const targetElement = elementRects.targetElements[this.sameHeightIdentifiers[v]];
                if (!targetElement) {
                    return;
                }
                targetElement.targetElement.style.minHeight = "auto";
            });
        }
    }, 400);

    private getDebouncedRects = debounce(() => {
        this.getElementsRects();
    }, 400);

    @Listen("resize", { target: "window" })
    async recalculateHeights() {
        this.removeHeights();
        this.getDebouncedRects();
    }

    private preventVerticalMarginCollapse = (targetElement: HTMLElement) => {
        const targetElementStyles = window.getComputedStyle(targetElement);
        const paddingTop = targetElementStyles.getPropertyValue("padding-top");
        const paddingBottom = targetElementStyles.getPropertyValue("padding-bottom");
        if (paddingTop === "0px") {
            targetElement.style.paddingTop = "0.05px";
        }
        if (paddingBottom === "0px") {
            targetElement.style.paddingBottom = "0.05px";
        }
    };

    render() {
        return (
            <Host class={this.baseClass}>
                <slot />
            </Host>
        );
    }
}
